import { normalize, schema } from 'normalizr';
import { SuperAgentStatic } from 'superagent';
import { SPAREMIN_SERVICES_THIRDPARTYAUTHENTICATION_URL } from 'config';
import { createRequest } from '../utils';
import {
  MY_SOCIAL_AUTH_URL,
  MY_THIRD_PARTY_AUTH_PATH,
  MY_THIRD_PARTY_CANVA_PATH,
  MY_THIRD_PARTY_FACEBOOK_PATH,
  MY_THIRD_PARTY_LINKEDIN_PATH,
  MY_THIRD_PARTY_TIKTOK_PATH,
  MY_THIRD_PARTY_YOUTUBE_PATH,
  THIRD_PARTY_SOCIAL_AUTH_PATH,
} from './constants';

import * as types from './types';

const youtubePlaylistsSchema = new schema.Entity('youtubePlaylist');

const getGettyProviderToken = (
  args: types.GetGettyProviderTokenArgs,
  request: SuperAgentStatic,
): Promise<types.GetGettyProviderTokenResult> => {
  const [provider] = args;

  return request
    .get(MY_THIRD_PARTY_AUTH_PATH)
    .query({ provider })
    .then(res => res.body);
};

const authorize = async (
  args: types.AuthorizeArgs,
  request: SuperAgentStatic,
): Promise<types.AuthorizeResult> => {
  const [provider] = args;

  await request.post(THIRD_PARTY_SOCIAL_AUTH_PATH).send({
    provider,
  });
};

async function getSocialAuthStatus(
  args: types.GetSocialAuthStatusArgs,
  request: SuperAgentStatic,
): Promise<types.GetSocialAuthStatusResult> {
  const [provider, scopeContext, forceLogin] = args;

  const { body } = await request
    .get(MY_SOCIAL_AUTH_URL)
    .withCredentials()
    .query({
      provider,
      scopeContext,
      forceLogin: String(!!forceLogin),
    });

  return body;
}

async function getSocialProfile(
  args: types.GetSocialProfileArgs,
  request: SuperAgentStatic,
): Promise<types.GetSocialProfileResult> {
  const [
    provider,
    providerUserId,
    idToken,
    accessToken,
    accessTokenSecret,
  ] = args;

  const { body } = await request.post(`${MY_SOCIAL_AUTH_URL}/profile`).send({
    provider,
    providerUserId,
    idToken,
    accessToken,
    accessTokenSecret,
  });

  return body;
}

async function getLinkedinOrganizations(
  args: types.GetLinkedinOrganizationsArgs,
  request: SuperAgentStatic,
): Promise<types.GetSocialProfileResult> {
  const [provider, providerUserId, idToken, accessToken] = args;

  const { body } = await request
    .post(`${MY_THIRD_PARTY_LINKEDIN_PATH}/organizations`)
    .send({
      provider,
      providerUserId,
      idToken,
      accessToken,
    });

  return body;
}

async function getInstagramAccounts(
  args: types.GetInstagramAccountsArgs,
  request: SuperAgentStatic,
): Promise<types.GetInstagramAccountsResult[]> {
  const [providerUserId, accessToken] = args;

  const { body } = await request
    .post(`${MY_THIRD_PARTY_FACEBOOK_PATH}/instagram-accounts`)
    .send({
      providerUserId,
      accessToken,
    });

  return body.data;
}

async function getFacebookPages(
  args: types.GetFacebookPagesArgs,
  request: SuperAgentStatic,
): Promise<types.GetFacebookPagesResult[]> {
  const [providerUserId, accessToken] = args;

  const { body } = await request
    .post(`${MY_THIRD_PARTY_FACEBOOK_PATH}/pages`)
    .send({
      providerUserId,
      accessToken,
    });

  return body.data;
}

export const getSocialYoutubeChannel = async (
  args: types.GetSocialYoutubeChannelArgs,
  request: SuperAgentStatic,
): Promise<types.GetSocialYoutubeChannelResult> => {
  const [providerUserId, accessToken] = args;

  const { body } = await request
    .post(`${MY_THIRD_PARTY_YOUTUBE_PATH}/channel`)
    .send({
      providerUserId,
      accessToken,
    });

  return body;
};

export const getAppsConnected = (
  args: types.GetAppsConnectedArgs,
  request: SuperAgentStatic,
): Promise<types.GetAppsConnectedResult> => {
  const { providers } = args;
  return request
    .get(`${MY_SOCIAL_AUTH_URL}/access`)
    .query({ provider: providers })
    .then(res => res.body);
};

export const deleteAppsConnected = async (
  args: types.DeleteAppsConnectedArgs,
  request: SuperAgentStatic,
): Promise<void> => {
  const { provider } = args;

  await request
    .delete(`${MY_SOCIAL_AUTH_URL}/access`)
    .query({ provider })
    .send();
};

export const getYoutubePlaylist = (
  args: types.GetYoutubePlaylistsArgs,
  request: SuperAgentStatic,
): Promise<types.GetPlaylistsResult> => {
  const { providerUserId, accessToken } = args;
  return request
    .post(`${MY_THIRD_PARTY_YOUTUBE_PATH}/playlists`)
    .set('Content-Type', 'application/json')
    .send({
      providerUserId,
      accessToken,
    })
    .then(res => res.body.data);
};

export const getTikTokCreatorInfo = (
  args: types.GetTikTokCreatorInfoArgs,
  request: SuperAgentStatic,
): Promise<types.GetTikTokCreatorInfoResult> => {
  const { providerUserId, accessToken } = args;

  return request
    .post(`${MY_THIRD_PARTY_TIKTOK_PATH}/creator-info`)
    .set('Content-Type', 'application/json')
    .send({
      providerUserId,
      accessToken,
    })
    .then(res => res.body);
};

export const getInstagramAccountInfo = (
  args: types.GetInstagramAccountInfoArgs,
  request: SuperAgentStatic,
): Promise<types.GetInstagramAccountInfoResult> => {
  const { providerUserId, accessToken, instagramId } = args;

  return request
    .post(`${MY_THIRD_PARTY_FACEBOOK_PATH}/instagram-account-info`)
    .set('Content-Type', 'application/json')
    .send({
      providerUserId,
      accessToken,
      instagramId,
    })
    .then(res => res.body);
};

export const createCanvaDesign = (
  args: types.CreateCanvaDesignArgs,
  request: SuperAgentStatic,
): Promise<types.CreateCanvaDesignResult> => {
  return request
    .post(`${MY_THIRD_PARTY_CANVA_PATH}/create-design`)
    .set('Content-Type', 'application/json')
    .send(args)
    .then(res => res.body);
};

export const createCanvaExport = (
  args: types.CreateCanvaExportArgs,
  request: SuperAgentStatic,
): Promise<types.CanvaExportResult> => {
  const { designId, ...rest } = args;

  return request
    .post(`${MY_THIRD_PARTY_CANVA_PATH}/designs/${designId}/create-export`)
    .set('Content-Type', 'application/json')
    .send(rest)
    .then(res => res.body);
};

export const getCanvaExport = (
  args: types.GetCanvaExportArgs,
  request: SuperAgentStatic,
): Promise<types.CanvaExportResult> => {
  const { exportId, ...rest } = args;

  return request
    .post(`${MY_THIRD_PARTY_CANVA_PATH}/exports/${exportId}`)
    .set('Content-Type', 'application/json')
    .send(rest)
    .then(res => res.body);
};

async function createYoutubePlaylist(
  args: types.CreateYoutubePlaylistArgs,
  request: SuperAgentStatic,
): Promise<types.CreateYoutubePlaylistResult> {
  const { body } = await request
    .post(`${MY_THIRD_PARTY_YOUTUBE_PATH}/create-playlist`)
    .send(args);

  return normalize(body, youtubePlaylistsSchema);
}

export const handle = (
  method: types.ServiceMethod,
  args: any,
  token?: string,
): Promise<any> => {
  const request = createRequest({
    token,
    baseUrl: SPAREMIN_SERVICES_THIRDPARTYAUTHENTICATION_URL,
  });

  switch (method) {
    case types.ServiceMethod.GET_YOUTUBE_PLAYLISTS:
      return getYoutubePlaylist(args, request);

    case types.ServiceMethod.CREATE_YOUTUBE_PLAYLIST:
      return createYoutubePlaylist(args, request);

    case types.ServiceMethod.GET_TIKTOK_CREATOR_INFO:
      return getTikTokCreatorInfo(args, request);

    case types.ServiceMethod.GET_INSTAGRAM_ACCOUNT_INFO:
      return getInstagramAccountInfo(args, request);

    case types.ServiceMethod.GET_GETTY_PROVIDER_TOKEN:
      return getGettyProviderToken(args, request);

    case types.ServiceMethod.AUTHORIZE:
      return authorize(args, request);

    case types.ServiceMethod.GET_SOCIAL_PROFILE:
      return getSocialProfile(args, request);

    case types.ServiceMethod.GET_LINKEDIN_ORGANIZATIONS:
      return getLinkedinOrganizations(args, request);

    case types.ServiceMethod.GET_INSTAGRAM_ACCOUNTS:
      return getInstagramAccounts(args, request);

    case types.ServiceMethod.GET_FACEBOOK_PAGES:
      return getFacebookPages(args, request);

    case types.ServiceMethod.GET_SOCIAL_AUTH_STATUS:
      return getSocialAuthStatus(args, request);

    case types.ServiceMethod.GET_SOCIAL_YOUTUBE_CHANNEL:
      return getSocialYoutubeChannel(args, request);

    case types.ServiceMethod.GET_APPS_CONNECTED:
      return getAppsConnected(args, request);

    case types.ServiceMethod.DELETE_APPS_CONNECTED:
      return deleteAppsConnected(args, request);

    case types.ServiceMethod.CREATE_CANVA_DESIGN:
      return createCanvaDesign(args, request);

    case types.ServiceMethod.CREATE_CANVA_EXPORT:
      return createCanvaExport(args, request);

    case types.ServiceMethod.GET_CANVA_EXPORT:
      return getCanvaExport(args, request);

    default:
      throw new Error(`${types.ACTION_KEY} cannot handle method ${method}`);
  }
};
